import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    author: 'Indicraft Team',
    published: true,
    seo_title: '',
    seo_description: '',
    seo_keywords: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog?published_only=false`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        featured_image: post.featured_image || '',
        author: post.author,
        published: post.published,
        seo_title: post.seo_title || '',
        seo_description: post.seo_description || '',
        seo_keywords: post.seo_keywords || ''
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        featured_image: '',
        author: 'Indicraft Team',
        published: true,
        seo_title: '',
        seo_description: '',
        seo_keywords: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/api/admin/blog/${editingPost.id}`, formData);
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API_URL}/api/admin/blog`, formData);
        toast.success('Blog post created!');
      }
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      console.error('Failed to save post:', error);
      toast.error('Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blog/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="blog" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]">Blog Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => handleOpenDialog()} className="bg-[#064E3B] hover:bg-[#065F46]">
              <Plus className="w-5 h-5 mr-2" />
              New Post
            </Button>
            <Button onClick={handleLogout} variant="ghost">
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <p className="font-semibold text-[#0f172a]">{post.title}</p>
                      <p className="text-sm text-zinc-500">{post.slug}</p>
                    </td>
                    <td>{post.author}</td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        post.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>{new Date(post.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(post)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Blog Post' : 'New Blog Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
            </div>
            <div>
              <Label>Excerpt</Label>
              <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Content</Label>
              <RichTextEditor value={formData.content} onChange={(content) => setFormData({ ...formData, content })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Featured Image URL</Label>
                <Input value={formData.featured_image} onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })} />
              </div>
              <div>
                <Label>Author</Label>
                <Input value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>SEO Title</Label>
              <Input value={formData.seo_title} onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} />
            </div>
            <div>
              <Label>SEO Description</Label>
              <Textarea value={formData.seo_description} onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>SEO Keywords</Label>
              <Input value={formData.seo_keywords} onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })} placeholder="keyword1, keyword2, keyword3" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({ ...formData, published: checked })} />
              <Label>Published</Label>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={loading} className="bg-[#064E3B]">{loading ? 'Saving...' : 'Save Post'}</Button>
              <Button onClick={() => setDialogOpen(false)} variant="outline">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlog;
